import { useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionHeading from '@/components/feature/SectionHeading';
import TimelineSection from '@/pages/home/components/TimelineSection';
import Testimonials from '@/pages/home/components/Testimonials';
import { SITE, PRINCIPLES, STATS } from '@/mocks/site';

export default function About() {
  const jsonLd = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于黑料百科',
        description:
          '黑料百科是一支专门整理中文互联网冷门、小众、被删与尘封内容的线上档案队，坚持标注材料来源、不做隐私挖掘。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE.name,
        alternateName: SITE.englishName,
        description: SITE.intro,
        foundingDate: SITE.founded,
        email: SITE.email,
        knowsAbout: ['网络文化考古', '早期论坛', '被删内容存档', '小众亚文化', '网页存档'],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: '/about' },
        ],
      },
    ],
    [],
  );

  return (
    <>
      <Seo
        title="关于我们 | 黑料百科 · 网络内容考古档案队"
        description="了解黑料百科的定位、发展历程与内容原则：我们只整理公开传播过的网络文化内容，标注材料来源与复原程度，不做任何个人隐私挖掘。"
        keywords="关于黑料百科,内容原则,网络考古,档案队,材料来源"
        path="/about"
        ogType="website"
        jsonLd={jsonLd}
      />
      <ScrollToTop />
      <Navbar />

      <main className="w-full pt-[68px] md:pt-[74px]">
        <section className="w-full border-b border-background-200 bg-background-100 archive-grain">
          <div className="w-full px-4 md:px-8 py-10 md:py-16">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <h1 className="mt-5 max-w-3xl font-heading text-3xl md:text-5xl leading-tight text-foreground-950">
              关于黑料百科：一支专门整理「消失内容」的档案队
            </h1>
            <p className="mt-5 max-w-3xl text-sm md:text-base text-foreground-600 leading-relaxed">
              {SITE.intro}
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              <div className="lg:col-span-5">
                <div className="w-full aspect-[4/5] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=overhead%20view%20of%20a%20dark%20archival%20worktable%20covered%20with%20old%20photographs%2C%20printed%20web%20screenshots%2C%20floppy%20disks%20and%20a%20magnifying%20glass%2C%20warm%20amber%20desk%20lamp%20light%2C%20dust%20particles%2C%20meticulous%20research%20mood%2C%20deep%20brown%20and%20gold%20tones&width=900&height=1120&seq=hlkb-about-01&orientation=portrait"
                    alt="档案工作台上摊开的旧照片、网页打印稿与软盘"
                    title="黑料百科档案整理工作台"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="lg:col-span-7">
                <SectionHeading
                  eyebrow="OUR STANCE"
                  title="我们为什么只做「考古」"
                  description={SITE.positioning}
                />
                <div className="mt-7 space-y-5 text-sm md:text-base text-foreground-600 leading-loose">
                  <p>
                    互联网的记忆比想象中脆弱。一次服务器迁移、一个域名到期，几个月后连搜索结果都翻不到痕迹。
                    时间越久，能佐证的材料越少，最后只剩下几句似是而非的转述。
                  </p>
                  <p>
                    所以我们做的事很具体：趁材料还在，把它编号、归档、标注来源与复原程度，
                    并写清楚哪一部分是确证的、哪一部分仍属存疑。与其说是内容站，不如说是一份持续更新的公开笔记。
                  </p>
                  <p>
                    我们关注的始终是公开传播过的网络文化本身，不收集、不发布任何人的隐私信息。
                    遇到只能指向具体个人的内容，档案员会主动收窄范围，只保留与公共议题相关的部分。
                  </p>
                </div>

                <dl className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-6">
                  {STATS.map((item) => (
                    <div key={item.label}>
                      <dt className="text-xs text-foreground-600">{item.label}</dt>
                      <dd className="mt-1.5 font-heading text-2xl text-primary-500">
                        {item.value}
                        <span className="text-xs text-foreground-600 ml-1">{item.unit}</span>
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 border-y border-background-200 py-16 md:py-24">
          <div className="w-full px-4 md:px-8">
            <SectionHeading
              eyebrow="PRINCIPLES"
              title="四条一直没变过的内容原则"
              description="它们决定了哪些材料可以入库，以及入库之后要怎么标注。"
              align="center"
            />
            <div className="mt-10 md:mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {PRINCIPLES.map((item, index) => (
                <div
                  key={item.title}
                  className="relative flex flex-col h-full p-6 rounded-lg bg-background-50 border border-background-200"
                >
                  <span className="absolute top-6 right-6 font-heading text-3xl text-background-300">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500/10 border border-accent-500/30 text-accent-500 text-xl">
                    <i className={item.icon}></i>
                  </span>
                  <h3 className="mt-5 font-heading text-lg text-foreground-950">{item.title}</h3>
                  <p className="mt-2.5 text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <TimelineSection />
        <Testimonials />

        <section id="contact" className="w-full bg-background-50 py-16 md:py-24 scroll-mt-20">
          <div className="w-full px-4 md:px-8">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                <div className="lg:col-span-7">
                  <SectionHeading
                    eyebrow="CONTACT"
                    title="联系方式与线索投递"
                    description="无论你是想补充一份材料，还是发现某条档案需要更正，都可以直接写信给我们。工作日通常会在 48 小时内回复。"
                  />
                </div>
                <div className="lg:col-span-5 flex flex-col gap-4">
                  <a
                    href={`mailto:${SITE.email}`}
                    className="group flex items-center justify-between gap-4 p-5 rounded-md bg-background-50 border border-background-200 hover:border-primary-500 transition-colors cursor-pointer"
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-500/10 border border-primary-500/25 text-primary-500 text-lg">
                        <i className="ri-mail-line"></i>
                      </span>
                      <span className="flex flex-col">
                        <span className="text-xs text-foreground-600">档案室邮箱</span>
                        <span className="text-sm text-foreground-900">{SITE.email}</span>
                      </span>
                    </span>
                    <i className="ri-arrow-right-up-line text-foreground-500 group-hover:text-primary-500 transition-colors"></i>
                  </a>
                  <a
                    href={`mailto:${SITE.submitEmail}`}
                    className="group flex items-center justify-between gap-4 p-5 rounded-md bg-background-50 border border-background-200 hover:border-accent-500 transition-colors cursor-pointer"
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500/10 border border-accent-500/25 text-accent-500 text-lg">
                        <i className="ri-send-plane-line"></i>
                      </span>
                      <span className="flex flex-col">
                        <span className="text-xs text-foreground-600">线索投递</span>
                        <span className="text-sm text-foreground-900">{SITE.submitEmail}</span>
                      </span>
                    </span>
                    <i className="ri-arrow-right-up-line text-foreground-500 group-hover:text-accent-500 transition-colors"></i>
                  </a>
                  <div className="flex items-center gap-3 px-5 py-4 rounded-md bg-background-50 border border-background-200">
                    <span className="w-10 h-10 flex items-center justify-center rounded-md bg-secondary-100 text-secondary-900 text-lg">
                      <i className="ri-map-pin-2-line"></i>
                    </span>
                    <span className="flex flex-col">
                      <span className="text-xs text-foreground-600">协作方式</span>
                      <span className="text-sm text-foreground-900">{SITE.location}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}